import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Image } from '@common/core/models';

export interface IImageZoomViewerDialogData {
  images: Image[];
  startIndex: number;
}

type EImageSourceStep = 'original' | 'xlarge' | 'placeholder';

const PLACEHOLDER_IMAGE_URL = '/assets/img/placeholder-image.png';
const ZOOMABLE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const DOUBLE_TAP_MAX_DELAY = 350;
const DOUBLE_TAP_MAX_DISTANCE = 40;
const PINCH_ZOOM_FACTOR = 1.5;
const WHEEL_ZOOM_FACTOR = 0.002;
const INITIAL_ZOOM_FACTOR = 2;

@Component({
  selector: 'mpa-image-zoom-viewer',
  templateUrl: './image-zoom-viewer.component.html',
  styleUrls: ['./image-zoom-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ImageZoomViewerComponent implements AfterViewInit {
  @ViewChild('viewport') viewport: ElementRef<HTMLDivElement>;
  @ViewChild('imageElement') imageElement: ElementRef<HTMLImageElement>;

  images: Image[] = [];
  currentIndex = 0;
  imageUrl: string;
  loading = true;
  isPanning = false;

  fitWidth = 0;
  fitHeight = 0;
  scale = 1;
  minScale = 1;
  maxScale = 4;
  tx = 0;
  ty = 0;

  private imageNaturalWidth = 0;
  private imageNaturalHeight = 0;
  private imageSourceStep: EImageSourceStep = 'original';
  private pointers = new Map<number, { x: number; y: number }>();
  private panStart: { x: number; y: number; tx: number; ty: number };
  private pinchStart: { distance: number; scale: number };
  private lastPinchMid: { x: number; y: number };
  private lastTapTime = 0;
  private lastTapX = 0;
  private lastTapY = 0;
  private pointerDownInfo: { x: number; y: number; time: number; moved: boolean };

  get currentImage(): Image {
    return this.images[this.currentIndex];
  }

  get hasMultipleImages(): boolean {
    return this.images.length > 1;
  }

  get naturalWidth(): number {
    return this.imageNaturalWidth;
  }

  get naturalHeight(): number {
    return this.imageNaturalHeight;
  }

  get fileSizeBytes(): number {
    if (this.imageSourceStep !== 'original') {
      return undefined;
    }
    // NOTE: strapi saves the size in kilobytes
    const size = this.currentImage?.size;
    return size ? size * 1024 : undefined;
  }

  constructor(
    public dialogRef: MatDialogRef<ImageZoomViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: IImageZoomViewerDialogData
  ) {}

  ngOnInit(): void {
    this.images = this.dialogData?.images || [];
    this.currentIndex = Math.min(
      Math.max(this.dialogData?.startIndex || 0, 0),
      Math.max(this.images.length - 1, 0)
    );
    this.loadCurrentImage();
  }

  ngAfterViewInit(): void {
    const image = this.imageElement?.nativeElement;
    if (image?.complete && image.naturalWidth) {
      this.onImageLoad();
    }
  }

  getTransform(): string {
    return `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onPreviousImage(): void {
    if (this.currentIndex > 0) {
      this.setImage(this.currentIndex - 1);
    }
  }

  onNextImage(): void {
    if (this.currentIndex < this.images.length - 1) {
      this.setImage(this.currentIndex + 1);
    }
  }

  onZoomIn(): void {
    this.zoomAt(null, null, this.scale * PINCH_ZOOM_FACTOR);
  }

  onZoomOut(): void {
    this.zoomAt(null, null, this.scale / PINCH_ZOOM_FACTOR);
  }

  onReset(): void {
    this.scale = this.minScale;
    this.tx = 0;
    this.ty = 0;
  }

  onImageLoad(): void {
    const image = this.imageElement?.nativeElement;
    this.imageNaturalWidth = image?.naturalWidth || 0;
    this.imageNaturalHeight = image?.naturalHeight || 0;
    this.loading = false;
    this.fitToScreen();
    this.zoomAt(null, null, Math.min(this.minScale * INITIAL_ZOOM_FACTOR, this.maxScale));
  }

  onImageError(): void {
    if (this.imageSourceStep === 'original') {
      this.imageSourceStep = 'xlarge';
      const xlargeUrl = this.currentImage?.getImageByFormat('xlarge')?.url;
      if (xlargeUrl && xlargeUrl !== this.imageUrl) {
        this.imageUrl = xlargeUrl;
        return;
      }
    }
    if (this.imageSourceStep !== 'placeholder') {
      this.imageSourceStep = 'placeholder';
      this.imageUrl = PLACEHOLDER_IMAGE_URL;
      return;
    }
    this.loading = false;
  }

  onPointerDown(event: PointerEvent): void {
    if (this.loading || (event.target as HTMLElement).closest('button')) {
      return;
    }
    this.viewport.nativeElement.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 1) {
      this.panStart = { x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty };
      this.pointerDownInfo = { x: event.clientX, y: event.clientY, time: Date.now(), moved: false };
      this.isPanning = true;
    } else if (this.pointers.size === 2) {
      const [first, second] = [...this.pointers.values()];
      this.pinchStart = {
        distance: Math.max(Math.hypot(first.x - second.x, first.y - second.y), 1),
        scale: this.scale,
      };
      this.panStart = null;
      this.lastPinchMid = null;
      this.pointerDownInfo = null;
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId)) {
      return;
    }
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pinchStart && this.pointers.size >= 2) {
      const [first, second] = [...this.pointers.values()];
      const distance = Math.max(Math.hypot(first.x - second.x, first.y - second.y), 1);
      const midX = (first.x + second.x) / 2;
      const midY = (first.y + second.y) / 2;
      this.zoomAt(midX, midY, this.pinchStart.scale * (distance / this.pinchStart.distance));
      if (this.lastPinchMid) {
        this.tx += midX - this.lastPinchMid.x;
        this.ty += midY - this.lastPinchMid.y;
        this.clampPan();
      }
      this.lastPinchMid = { x: midX, y: midY };
    } else if (this.panStart) {
      const deltaX = event.clientX - this.panStart.x;
      const deltaY = event.clientY - this.panStart.y;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        if (this.pointerDownInfo) {
          this.pointerDownInfo.moved = true;
        }
      }
      this.tx = this.panStart.tx + deltaX;
      this.ty = this.panStart.ty + deltaY;
      this.clampPan();
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId)) {
      return;
    }
    this.pointers.delete(event.pointerId);
    if (this.viewport.nativeElement.hasPointerCapture(event.pointerId)) {
      this.viewport.nativeElement.releasePointerCapture(event.pointerId);
    }
    this.pinchStart = null;
    this.lastPinchMid = null;
    this.panStart = null;
    this.isPanning = false;
    if (this.pointers.size === 1) {
      const [remaining] = [...this.pointers.values()];
      this.panStart = { x: remaining.x, y: remaining.y, tx: this.tx, ty: this.ty };
      this.isPanning = true;
    } else if (this.pointers.size === 2) {
      const [first, second] = [...this.pointers.values()];
      this.pinchStart = {
        distance: Math.max(Math.hypot(first.x - second.x, first.y - second.y), 1),
        scale: this.scale,
      };
    } else if (this.pointers.size === 0) {
      this.detectTap(event);
    }
  }

  onPointerCancel(event: PointerEvent): void {
    this.pointers.delete(event.pointerId);
    this.pinchStart = null;
    this.lastPinchMid = null;
    this.panStart = null;
    this.isPanning = false;
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    event.preventDefault();
    if (this.loading || !this.fitWidth) {
      return;
    }
    const deltaY = event.deltaMode === 1 ? event.deltaY * 33 : event.deltaY;
    this.zoomAt(event.clientX, event.clientY, this.scale * Math.exp(-deltaY * WHEEL_ZOOM_FACTOR));
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.onZoomIn();
    } else if (event.key === '-') {
      event.preventDefault();
      this.onZoomOut();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.onPreviousImage();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.onNextImage();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.fitToScreen();
  }

  private setImage(index: number): void {
    this.currentIndex = index;
    this.loadCurrentImage();
  }

  private loadCurrentImage(): void {
    this.loading = true;
    this.imageSourceStep = 'original';
    this.imageUrl = this.getLargestImageUrl(this.currentImage);
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
  }

  private getLargestImageUrl(image: Image): string {
    const ext = (image?.ext || '').toLowerCase().replace(/^\./, '');
    if (image?.url && ZOOMABLE_EXTENSIONS.includes(ext)) {
      this.imageSourceStep = 'original';
      return image.url;
    }
    this.imageSourceStep = 'xlarge';
    return image?.getImageByFormat('xlarge')?.url;
  }

  private fitToScreen(): void {
    const containerWidth = this.containerWidth;
    const containerHeight = this.containerHeight;
    if (!containerWidth || !containerHeight || !this.imageNaturalWidth || !this.imageNaturalHeight) {
      return;
    }
    // NOTE: the image element is rendered at fit size, so scale 1 means fit to screen.
    // maxScale is intentionally unbounded: the user is allowed to zoom past the native
    // resolution even if the image becomes pixelated
    const fitScale = Math.min(
      containerWidth / this.imageNaturalWidth,
      containerHeight / this.imageNaturalHeight
    );
    this.minScale = 1;
    this.maxScale = Infinity;
    this.fitWidth = this.imageNaturalWidth * fitScale;
    this.fitHeight = this.imageNaturalHeight * fitScale;
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
  }

  private get containerWidth(): number {
    return this.viewport?.nativeElement?.clientWidth || 0;
  }

  private get containerHeight(): number {
    return this.viewport?.nativeElement?.clientHeight || 0;
  }

  private zoomAt(clientX: number | null, clientY: number | null, newScale: number): void {
    newScale = Math.min(Math.max(newScale, this.minScale), this.maxScale);
    if (newScale === this.scale) {
      this.clampPan();
      return;
    }
    let containerX: number;
    let containerY: number;
    if (clientX === null || clientY === null || !this.viewport?.nativeElement) {
      // NOTE: coordinates are relative to the container center
      containerX = 0;
      containerY = 0;
    } else {
      const rect = this.viewport.nativeElement.getBoundingClientRect();
      containerX = clientX - rect.left - rect.width / 2;
      containerY = clientY - rect.top - rect.height / 2;
    }
    const ratio = newScale / this.scale;
    this.tx = containerX - (containerX - this.tx) * ratio;
    this.ty = containerY - (containerY - this.ty) * ratio;
    this.scale = newScale;
    this.clampPan();
  }

  private clampPan(): void {
    const maxTx = Math.max(0, (this.fitWidth * this.scale - this.containerWidth) / 2);
    const maxTy = Math.max(0, (this.fitHeight * this.scale - this.containerHeight) / 2);
    this.tx = Math.min(Math.max(this.tx, -maxTx), maxTx);
    this.ty = Math.min(Math.max(this.ty, -maxTy), maxTy);
  }

  private toggleZoom(clientX: number, clientY: number): void {
    if (this.scale > this.minScale * 1.01) {
      this.onReset();
    } else {
      this.zoomAt(clientX, clientY, this.minScale * 2.5);
    }
  }

  private detectTap(event: PointerEvent): void {
    if (!this.pointerDownInfo || this.pointerDownInfo.moved) {
      this.pointerDownInfo = null;
      this.lastTapTime = 0;
      return;
    }
    const now = Date.now();
    const isDoubleTap =
      now - this.lastTapTime < DOUBLE_TAP_MAX_DELAY &&
      Math.hypot(event.clientX - this.lastTapX, event.clientY - this.lastTapY) < DOUBLE_TAP_MAX_DISTANCE;
    if (isDoubleTap) {
      this.lastTapTime = 0;
      this.toggleZoom(event.clientX, event.clientY);
    } else {
      this.lastTapTime = now;
      this.lastTapX = event.clientX;
      this.lastTapY = event.clientY;
    }
    this.pointerDownInfo = null;
  }
}
